import React from "react";
import { AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

/**
 * Dialog "motif d'astreinte" — ouvert quand le backend rejette la soumission CRA
 * parce qu'il manque un motif sur des dates de weekend / jour férié.
 *
 * @param {Object|null} props.submitDialog  null = fermé
 * @param {(d:Object|null)=>void} props.setSubmitDialog
 * @param {(reasons:Object)=>void} props.onConfirm
 */
export default function OnCallDialog({ submitDialog, setSubmitDialog, onConfirm }) {
  return (
    <Dialog open={!!submitDialog} onOpenChange={(o) => !o && setSubmitDialog(null)}>
      <DialogContent className="max-w-xl" data-testid="on-call-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-600" /> On-call motif required
          </DialogTitle>
          <DialogDescription>
            You have imputations on weekend(s) or holiday(s). Provide a reason for each, or cancel the submission and clear the corresponding imputations.
          </DialogDescription>
        </DialogHeader>
        {submitDialog && (
          <div className="space-y-3 py-2 max-h-[360px] overflow-y-auto">
            {submitDialog.dates.map((d) => {
              const dateObj = new Date(d);
              const label = dateObj.toLocaleDateString("en-US", {
                weekday: "long", year: "numeric", month: "short", day: "2-digit",
              });
              const _holInfo = submitDialog.holidays[d];
              const isHoliday = !!_holInfo;
              const _holName = typeof _holInfo === "string" ? _holInfo : (_holInfo?.name || "");
              return (
                <div
                  key={d}
                  className="bg-zinc-50 border border-zinc-200 rounded-md p-3"
                  data-testid={`on-call-row-${d}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase ${
                      isHoliday ? "bg-amber-100 text-amber-900" : "bg-zinc-200 text-zinc-800"
                    }`}>
                      {isHoliday ? `Holiday · ${_holName}` : "Weekend"}
                    </span>
                    <span className="text-xs font-medium">{label}</span>
                  </div>
                  <Input
                    data-testid={`on-call-input-${d}`}
                    value={submitDialog.reasons[d] || ""}
                    onChange={(ev) => setSubmitDialog({
                      ...submitDialog,
                      reasons: { ...submitDialog.reasons, [d]: ev.target.value },
                    })}
                    placeholder="Motif d'astreinte / reason"
                  />
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <button
            onClick={() => setSubmitDialog(null)}
            className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md"
          >
            Cancel
          </button>
          <button
            data-testid="on-call-submit"
            onClick={() => {
              if (!submitDialog) return;
              const all = submitDialog.dates.every(
                (d) => (submitDialog.reasons[d] || "").trim().length > 0
              );
              if (!all) { toast.error("Provide a motif for every date"); return; }
              onConfirm(submitDialog.reasons);
            }}
            className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 inline-flex items-center gap-2"
          >
            <Send size={13} /> Confirm submission
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
